/**
 * Shared SWG Structures types (P14.1) — the client contract.
 *
 * The client RENDERS these replicated rows (lot budget, deeds, deployed
 * structures, decorations, access lists) and CALLS the reducers; every validity,
 * lot, maintenance, decay, and redeed decision is server-authoritative. Mirrors
 * the server `structures` tables. Civic types (city_hall, …) are placed via the
 * P14.2 city-zoning path, so they are excluded from the open deed vocabulary.
 */

import type { IdentityHex } from './identity.js';

/** The deed types a player may freely mint + deploy onto the Commons. */
export const STRUCTURE_TYPES = [
  'merchant_tent',
  'small_house',
  'medium_house',
  'large_house',
  'guild_hall',
  'factory',
  'extractor_small',
  'extractor_medium',
  'extractor_large',
  'harvester',
  // THE SWG SET (2026-09-23) — server/src/lib/structures.ts STRUCTURE_CATALOG is the authority.
  'xlarge_house', 'city_hall', 'cantina', 'hospital', 'bank', 'garage', 'theater', 'cloning', 'shuttleport',
  'civic_decoration', 'museum', 'bunker', 'outpost', 'turret', 'city_wall', 'store', 'market', 'garden',
  'statue', 'fountain', 'street_lamp', 'pool', 'city_console', 'spaceport', 'hotel', 'salon', 'mining_facility',
] as const;

/** Display names — a copy of server/src/lib/structures.ts STRUCTURE_LABELS (keys never change). */
export const STRUCTURE_LABELS: Record<string, string> = {
  merchant_tent: 'Merchant Tent', small_house: 'Small House', medium_house: 'Medium House', large_house: 'Large House',
  xlarge_house: 'Manor', guild_hall: 'Guild Hall', factory: 'Factory', harvester: 'Harvester',
  extractor_small: 'Small Extractor', extractor_medium: 'Medium Extractor', extractor_large: 'Large Extractor',
  city_hall: 'City Hall', cantina: 'Entertainment Center', hospital: 'Medical Center', bank: 'Bank',
  garage: 'Vehicle Garage', theater: 'Theater', cloning: 'Cloning Facility', shuttleport: 'Shuttle Port',
  civic_decoration: 'Civic Decoration', museum: 'Museum', bunker: 'Bunker', outpost: 'Outpost', turret: 'Defense Turret',
  city_wall: 'City Wall', store: 'General Store', market: 'Market', garden: 'Garden', statue: 'Statue',
  fountain: 'Fountain', street_lamp: 'Street Lamp', pool: 'Pool', city_console: 'City Console', spaceport: 'Space Port',
  hotel: 'Hotel', salon: 'Salon', mining_facility: 'Mining Facility',
};

/** Beds for versona residents per type (server residentCapacity; 0 = not a residence). */
export const RESIDENT_CAPACITY: Record<string, number> = {
  city_hall: 2, small_house: 2, medium_house: 4, large_house: 6, xlarge_house: 10, hotel: 12,
};

/** The civic / decor types the mayor (or a zoning grant) places from the treasury, with their rank gates. */
export const CIVIC_MIN_RANK: Record<string, number> = {
  city_hall: 1, civic_decoration: 1, garden: 1, statue: 1, fountain: 1, street_lamp: 1, city_console: 1, outpost: 1,
  cantina: 2, garage: 2, bank: 2, market: 2, turret: 2, city_wall: 2, bunker: 2,
  hospital: 3, cloning: 3, museum: 3, pool: 3, theater: 4, shuttleport: 4, spaceport: 5,
};
export type StructureType = (typeof STRUCTURE_TYPES)[number];

// ─── THE FOOTPRINT + THE DOOR (2026-09-23) — a MIRROR of server/src/lib/structures.ts ───────────────
// The cooked set's bbox × the catalog scale (out-player-city/bbox.json): half-extents along the structure's
// own X/Z before yaw, height in metres. The server's placementProblem judges overlap and the door with these
// same numbers; the client's placement map draws them. verify:placement-rules pins the two tables equal.
// EVERY deed type has its own row since 2026-10-01 (37): LEGACY_FOOTPRINT only answers an unknown type. A scale change
// in prefab_model must be followed here — verify:interior-coverage pins each row to the served model within 0.25 m.
export const STRUCTURE_FOOTPRINT: Record<string, { hx: number; hz: number; h: number }> = {
  city_hall: { hx: 22.9, hz: 14.25, h: 32 },
  small_house: { hx: 4.91, hz: 3.47, h: 6 },
  medium_house: { hx: 6.03, hz: 6.0, h: 7.2 },
  large_house: { hx: 7.39, hz: 5.22, h: 9.6 },
  xlarge_house: { hx: 7.14, hz: 6.88, h: 12 },
  cantina: { hx: 6.62, hz: 7.7, h: 8 },
  museum: { hx: 6.86, hz: 9.81, h: 12 },
  bunker: { hx: 4.49, hz: 4.52, h: 4.5 }, // 4 → 4.5 m (2026-10-01): it clears the 4.2 m interior floor by 7 %
  outpost: { hx: 1.41, hz: 1.61, h: 9 },
  turret: { hx: 1.66, hz: 1.15, h: 3.2 },
  city_wall: { hx: 4.87, hz: 1.05, h: 4.5 },
  store: { hx: 4.5, hz: 3.17, h: 5.3 }, // 5 → 5.3 m (2026-10-01): 6.34 m deep — it clears the 6 m interior floor by 5.7 %
  market: { hx: 8.03, hz: 3.05, h: 7 },
  garden: { hx: 2.52, hz: 2.25, h: 3 },
  statue: { hx: 0.94, hz: 0.94, h: 5 },
  fountain: { hx: 2.37, hz: 2.33, h: 3.5 },
  street_lamp: { hx: 0.58, hz: 0.37, h: 4.5 },
  garage: { hx: 5.77, hz: 4.32, h: 6 },
  pool: { hx: 4.96, hz: 3.15, h: 3 },
  city_console: { hx: 0.33, hz: 0.28, h: 1.6 },
  hospital: { hx: 6.6, hz: 9.2, h: 9 },
  shuttleport: { hx: 9.08, hz: 7.33, h: 10 },
  spaceport: { hx: 13.77, hz: 10.6, h: 14 },
  hotel: { hx: 5.62, hz: 5.49, h: 14 },
  salon: { hx: 2.46, hz: 2.54, h: 5 },
  factory: { hx: 9.09, hz: 6.75, h: 10 },
  mining_facility: { hx: 8.87, hz: 4.59, h: 14 },
  // The guild hall (2026-09-24): its GLB (1.90 × 1.58 × 1.73 units, collider bounds) at StructureRenderModule's ×50 —
  // it had no row, so the judge tested a 5 × 5 m box under a 95 × 87 m, 79 m tall hall.
  guild_hall: { hx: 47.4, hz: 43.3, h: 79.2 },
  // THE LEGACY FIVE at a real size (2026-10-01, the interiors programme): these models sat at default_scale 1 — 1.9 m
  // toys — while this table gave them the 10 × 10 × 6 legacy box (so the walking grid shelled a tent and a harvester
  // the client never opened). Rows = the served model × the catalog scale (out-player-city/apply-scales.sh, LEGACY).
  // 2026-10-01, the owner's three answers: the tent and the harvester wear their OWN generated models now (a stall a
  // person stands at; a four-legged field machine — out-civic-four/ingest-field.sh), and the large extractor stands
  // 10 m (was 12.3: the ladder is 5.4 / 8.5 / 10, still the widest by footprint).
  merchant_tent: { hx: 2.11, hz: 1.43, h: 3.8 },
  harvester: { hx: 3.47, hz: 3.68, h: 6.5 },
  extractor_small: { hx: 2.86, hz: 1.59, h: 5.4 },
  extractor_medium: { hx: 4.05, hz: 2.62, h: 8.5 },
  extractor_large: { hx: 5.41, hz: 4.26, h: 10 },
  // THE FOUR NEW MODELS (2026-10-01; out-civic-four/ingest-four.sh): the deed types that had no model at all.
  bank: { hx: 4.17, hz: 5.31, h: 9 },
  theater: { hx: 7.76, hz: 12.3, h: 16 },
  cloning: { hx: 4.51, hz: 5.9, h: 8 },
  civic_decoration: { hx: 1.22, hz: 1.22, h: 5 },
};
/** The largest footprint's half-diagonal: an overlap search must reach this far past its own box (a neighbour's
 *  box can stand that far from its centre — the guild hall's is 64 m, past the old `max(hx, hz) + 40` reach). */
export const FOOTPRINT_MAX_HALF_DIAG = Math.max(...Object.values(STRUCTURE_FOOTPRINT).map((f) => Math.hypot(f.hx, f.hz)), 7.1);
export const LEGACY_FOOTPRINT = { hx: 5, hz: 5, h: 6 };
/** How far in front of the door (the structure's local −Z face) the approach point is tested. */
export const DOOR_APPROACH_M = 2.5;
export function footprintOf(type: string): { hx: number; hz: number; h: number } { return STRUCTURE_FOOTPRINT[type] ?? LEGACY_FOOTPRINT; }
/** The door approach point: DOOR_APPROACH_M beyond the −Z face under three's yaw (local z → (sin, cos)). */
export function doorApproach(x: number, z: number, yaw: number, hz: number): { x: number; z: number } {
  const d = -(hz + DOOR_APPROACH_M);
  return { x: x + Math.sin(yaw) * d, z: z + Math.cos(yaw) * d };
}

// ─── THE MODEL YAW (2026-09-23) ───────────────────────────────────────────────────────────────────────
// Every generated set model was LOOKED at from +Z and −Z (verify/render-glb-views.mjs, 54 renders): the
// entrance — the City Hall's columned door, the houses' doors, the store front, the museum arch — is on the
// model's +Z face, and Atlas puts a structure's door (the interior frame, the door approach, the marker on
// the placement map) on local −Z. The renderer turns these models by π so the real door lands on −Z; the
// structure frame (`structure.yaw`, decorations, the interior) is untouched. The legacy deeds keep their own
// conventions (the guild hall flips its INTERIOR instead — see StructureRenderModule).
// ─── THE FURNISH PACKS (2026-09-23) — a MIRROR of server/src/lib/structures.ts FURNISH_PACK_PRICES ─────
// A player structure's interior is EMPTY until its owner buys a pack from the structure console; each pack
// seats a larger share of the purpose plan (`share` of every floor's slots, `floors` floors) and costs more
// SOV (a sink to the platform). Buying replaces the pack; clearing is free. Player decor must be removed first.
export interface FurnishPack { pack: number; label: string; priceSov: number; share: number; floors: number }
export const FURNISH_PACKS: readonly FurnishPack[] = [
  { pack: 1, label: 'Essentials', priceSov: 300, share: 0.3, floors: 1 },
  { pack: 2, label: 'Comfort', priceSov: 700, share: 0.5, floors: 2 },
  { pack: 3, label: 'Furnished', priceSov: 1400, share: 0.7, floors: 3 },
  { pack: 4, label: 'Appointed', priceSov: 2600, share: 0.85, floors: 3 },
  { pack: 5, label: 'Grand', priceSov: 4500, share: 1.0, floors: 3 },
];
export function furnishPack(pack: number): FurnishPack | null { return FURNISH_PACKS.find((p) => p.pack === pack) ?? null; }

export const SET_MODEL_TYPES: readonly string[] = [
  'city_hall', 'small_house', 'medium_house', 'large_house', 'xlarge_house', 'cantina', 'museum', 'bunker', 'outpost',
  'turret', 'city_wall', 'store', 'market', 'garden', 'statue', 'fountain', 'street_lamp', 'garage', 'pool',
  'city_console', 'hospital', 'shuttleport', 'spaceport', 'hotel', 'salon', 'factory', 'mining_facility',
  // The guild hall joins the convention (2026-09-24): its carved entrance is on the model's +Z face too (the interior used
  // to flip π to meet it); turned with the set, its door is on the structure frame's −Z like every deed — the door the
  // placement map draws and the server's door-approach rule tests.
  'guild_hall',
  // The three new buildings of 2026-10-01 (out-civic-four), LOOKED AT from +Z and −Z on the raw and the cooked meshes:
  // the bank's portico, vault emblem and steps, the theater's marquee and three doors, the cloning facility's canopied
  // sliding door and steps are all on the model's +Z face. The civic decoration (an obelisk, the same from +Z and −Z,
  // no door) is NOT here: it needs no turn.
  'bank', 'theater', 'cloning',
  // The merchant tent's own model (2026-10-01, out-civic-four/ingest-field.sh), LOOKED AT from +Z and −Z: the scalloped
  // valance, the counter's pennant and the produce barrels — the customer's side — are on the model's +Z face. The new
  // harvester is NOT here: its operator's cabinet (the teal gauge) is on the model's −Z face already.
  'merchant_tent',
];
const SET_MODEL_SET = new Set(SET_MODEL_TYPES);
export function modelYawOf(type: string): number { return SET_MODEL_SET.has(type) ? Math.PI : 0; }

/** Per-structure access levels. */
export type StructurePermLevel = 'owner' | 'admin' | 'entry' | 'banned';

/** A player's lot budget. Mirrors `lot_budget`. */
export interface LotBudget {
  identity: IdentityHex;
  lotsTotal: number;
  lotsUsed: number;
}

/** A deed / packed control-device in inventory. Mirrors `structure_deed`. */
export interface StructureDeed {
  deedId: bigint;
  owner: IdentityHex;
  structureType: string;
  lotsRequired: number;
  baseMaintPerHour: number;
  itemCap: number;
  isCivic: boolean;
  packed: boolean;
  storedMaint: bigint;
  noTrade: boolean;
}

/** A deployed structure in the Commons. Mirrors `structure`. */
export interface DeployedStructure {
  structureId: bigint;
  worldId: bigint;
  owner: IdentityHex;
  structureType: string;
  cellX: number;
  cellZ: number;
  posX: number;
  posZ: number;
  yaw: number;
  lotsUsed: number;
  maintPool: bigint;
  condition: number;
  itemCap: number;
  itemCount: number;
  cityId: bigint;
  outsideCity: boolean;
}

// ─── Reducer argument types ──────────────────────────────────────────────────

export interface GrantDeedArgs {
  structureType: string;
}
export interface DeployStructureArgs {
  deedId: bigint;
  posX: number;
  posZ: number;
  yaw: number;
}
export interface PayMaintenanceArgs {
  structureId: bigint;
  amount: bigint;
}
export interface RedeedStructureArgs {
  structureId: bigint;
  confirm: boolean;
}
